/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {PageTitle} from '../../../_metronic/layout/core'
import Chart from 'react-apexcharts'
import {getDashboardStats} from '../../../API/api-endpoint'
import {KTIcon} from '../../../_metronic/helpers'

interface DashboardStats {
  totalUsers: number
  onlineUsers: number
  todaysUsers: number
  graphData: {
    date: string
    count: number
  }[]
}

const DashboardPage: FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await getDashboardStats()
        if (response && response.data) {
           setStats(response.data)
        }
      } catch (error) {
        console.error('Failed to fetch dashboard stats', error)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  if (loading) {
    return <div>Loading...</div>
  }

  if (!stats) {
    return <div>Unable to load dashboard data.</div>
  }

  const chartOptions = {
    chart: {
      id: 'user-registration-chart',
      toolbar: {
        show: false,
      },
    },
    xaxis: {
      categories: stats.graphData.map((d) => d.date),
      title: {
        text: 'Date',
      },
    },
    yaxis: {
      title: {
        text: 'New Users',
      },
    },
    stroke: {
      curve: 'smooth' as const, // explicitly typed as const for ApexCharts typpings
    },
    dataLabels: {
      enabled: false,
    },
    colors: ['#009EF7'],
  }

  const chartSeries = [
    {
      name: 'Registrations',
      data: stats.graphData.map((d) => d.count),
    },
  ]

  return (
    <>
      <div className='row g-5 g-xl-8'>
        {/* Total Users */}
        <div className='col-xl-4'>
          <div className='card bg-primary hoverable mb-5 mb-xl-8'>
            <div className='card-body py-4 d-flex justify-content-between align-items-center'>
              <div className='d-flex flex-column'>
                 <div className='fw-bold text-white mb-2'>Total Users</div>
                 <KTIcon iconName='profile-user' className='text-white fs-2x ms-n1' />
              </div>
              <div className='text-white fw-bold fs-1'>{stats.totalUsers}</div>
            </div>
          </div>
        </div>

        {/* Online Users */}
        <div className='col-xl-4'>
          <div className='card bg-success hoverable mb-5 mb-xl-8'>
            <div className='card-body py-4 d-flex justify-content-between align-items-center'>
              <div className='d-flex flex-column'>
                <div className='fw-bold text-white mb-2'>Online Users</div>
                <KTIcon iconName='wifi-home' className='text-white fs-2x ms-n1' />
              </div>
              <div className='text-white fw-bold fs-1'>{stats.onlineUsers}</div>
            </div>
          </div>
        </div>

        {/* Today's Users */}
        <div className='col-xl-4'>
           <div className='card bg-info hoverable mb-5 mb-xl-8'>
            <div className='card-body py-4 d-flex justify-content-between align-items-center'>
               <div className='d-flex flex-column'>
                 <div className='fw-bold text-white mb-2'>Today's Users</div>
                 <KTIcon iconName='calendar-8' className='text-white fs-2x ms-n1' />
               </div>
               <div className='text-white fw-bold fs-1'>{stats.todaysUsers}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Graph */}
      <div className='row g-5 g-xl-8'>
        <div className='col-xl-12'>
          <div className='card card-xl-stretch mb-xl-8'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-3 text-dark'>
                  User Registrations – Last 30 Days
                </span>
              </h3>
            </div>
            <div className='card-body'>
              <Chart options={chartOptions} series={chartSeries} type='line' height={350} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const DashboardWrapper: FC = () => {
  const intl = useIntl()
  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.DASHBOARD'})}</PageTitle>
      <DashboardPage />
    </>
  )
}

export {DashboardWrapper}
